import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { numberToRoman, romanToNumber, dateToRoman, dateError, romanGlyphs, romanBreakdown, DATE_SEPARATORS, MAX_ROMAN, type DateOrder, type DateSeparator } from "./roman";
import { copyText } from "@/lib/copyText";
import RomanGuide from "./RomanGuide";

const RomanExtras = lazy(() => import("./RomanExtras"));

const SEP_LABEL: Record<DateSeparator, string> = { ".": "dot .", "·": "middle dot ·", "/": "slash /", "-": "hyphen -", " ": "space" };

/** A numeral with its bars drawn as overlines (the copied text uses the Unicode combining overline). */
function RomanText({ text }: { text: string }) {
  return <>{romanGlyphs(text).map((g, i) => <span key={i} className={g.bar ? "overline" : undefined}>{g.ch}</span>)}</>;
}

const TOOL_INFO_CONTENT = {
  what: `Converts numbers to Roman numerals and Roman numerals back to regular numbers, in either direction.`,
  how: `Type a number in the left box to see its Roman numeral, with the working below, or a Roman numeral in the right box to see the number. The two boxes work separately. The date box turns a day, month and year into Roman numerals with the separator you pick. A reference table for 1 to 100 and for years sits below.`,
  tip: `Plain letters stop at 3,999 (MMMCMXCIX). Above that a bar over a letter multiplies it by 1,000, so V with a bar is 5,000, and the limit here is 3,999,999. The bar is written with the Unicode combining overline when you copy, so it may look slightly off in some fonts. Type a barred numeral with bars or in brackets: (V) is 5,000.`,
};

export default function RomanNumeralConverter() {
  const [number, setNumber] = useState("2026");
  const [roman, setRoman] = useState("MMXXVI");
  const [date, setDate] = useState(() => { const n = new Date(); return { d: String(n.getDate()), m: String(n.getMonth() + 1), y: String(n.getFullYear()) }; });

  const [order, setOrder] = useState<DateOrder>("dmy");
  const [sep, setSep] = useState<DateSeparator>(".");
  const toR = useMemo(() => numberToRoman(number), [number]);
  const toN = useMemo(() => romanToNumber(roman), [roman]);
  const romanResult = toR && "roman" in toR ? toR.roman : "";
  const vinculum = !!(toR && "vinculum" in toR && toR.vinculum);
  const numberForSteps = romanResult ? Number(number.trim().replace(/[\s_,]/g, "")) : null;
  const numberResult = toN && "value" in toN ? toN.value : null;
  const dateRoman = dateToRoman(Number(date.d), Number(date.m), Number(date.y), order, sep);
  const dateErr = dateError(date.d, date.m, date.y);

  const copy = (value: string) => {
    void copyText(value, "Copied!");
  };

  const { blocked, gate } = useLogToolUsage(romanResult, undefined, { ownActions: true });


  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0">
        <div>
          <Label className="mb-2 block">Number → Roman Numeral</Label>
          <Input
            inputMode="numeric"
            aria-label="Number"
            value={number}
            onChange={e => setNumber(e.target.value)}
            placeholder={`Enter a number (1 to ${MAX_ROMAN.toLocaleString("en-US")})`}
          />
          {toR && "error" in toR && <p className="text-xs text-destructive mt-1" data-testid="rn-error-num">{toR.error}</p>}
          <Card className="mt-3 p-4 text-center relative">
            <p className="text-2xl font-bold text-primary">
              {blocked ? "—" : romanResult ? <RomanText text={romanResult} /> : "—"}
            </p>
            {romanResult && vinculum && !blocked && <p className="text-xs text-muted-foreground mt-2" data-testid="rn-vinculum-note">A bar over a letter multiplies it by 1,000: {romanBreakdown(numberForSteps ?? 0)}.</p>}
            {romanResult && !blocked && (
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7" aria-label="Copy Roman numeral" onClick={() => copy(romanResult)}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            )}
          </Card>
        </div>
        <div>
          <Label className="mb-2 block">Roman Numeral → Number</Label>
          <Input
            aria-label="Roman numeral"
            value={roman}
            onChange={e => setRoman(e.target.value)}
            placeholder="e.g. MMXXVI"
          />
          {toN && "error" in toN && <p className="text-xs text-destructive mt-1" data-testid="rn-error-roman">{toN.error}</p>}
          {toN && "vinculum" in toN && toN.vinculum && <p className="text-xs text-muted-foreground mt-1">The barred letters are multiplied by 1,000.</p>}
          {toN && "clock" in toN && toN.clock && <p className="text-xs text-muted-foreground mt-1">IIII is the clock-face way of writing 4 (the standard form is IV).</p>}
          <Card className="mt-3 p-4 text-center relative">
            <p className="text-2xl font-bold text-primary">
              {blocked ? "—" : (numberResult !== null ? numberResult : "—")}
            </p>
            {numberResult !== null && !blocked && (
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7" aria-label="Copy number" onClick={() => copy(String(numberResult))}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            )}
          </Card>
        </div>
      </div>
      <div className="mt-6">
        <Label className="mb-2 block">Date → Roman numerals ({order === "mdy" ? "month, day, year" : "day, month, year"})</Label>
        <div className="flex gap-2 items-center flex-wrap min-w-0">
          <Input className="w-20" inputMode="numeric" aria-label="Day" aria-invalid={!!dateErr} value={date.d} onChange={e => setDate(p => ({ ...p, d: e.target.value }))} />
          <Input className="w-20" inputMode="numeric" aria-label="Month" aria-invalid={!!dateErr} value={date.m} onChange={e => setDate(p => ({ ...p, m: e.target.value }))} />
          <Input className="w-28" inputMode="numeric" aria-label="Year" aria-invalid={!!dateErr} value={date.y} onChange={e => setDate(p => ({ ...p, y: e.target.value }))} />
          <span className="text-lg font-bold text-primary break-all min-w-0" data-testid="rn-date">{blocked ? "—" : (dateRoman ?? "—")}</span>
          {dateRoman && !blocked && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copy(dateRoman)} aria-label="Copy date"><Copy className="h-3.5 w-3.5" /></Button>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm" role="radiogroup" aria-label="Date order">
          <label className="flex items-center gap-1.5"><input type="radio" name="rn-order" checked={order === "dmy"} onChange={() => setOrder("dmy")} />Day.Month.Year</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="rn-order" checked={order === "mdy"} onChange={() => setOrder("mdy")} />Month.Day.Year (US)</label>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <Label htmlFor="rn-sep" className="text-sm font-normal">Separator</Label>
          <select id="rn-sep" value={sep} onChange={(e) => setSep(e.target.value as DateSeparator)} className="border rounded-md px-2 py-1 bg-background text-sm" data-testid="rn-sep">
            {DATE_SEPARATORS.map((s) => <option key={s} value={s}>{SEP_LABEL[s]}</option>)}
          </select>
          <span className="text-xs text-muted-foreground">Dots are the usual style; tattoos and certificates often use a middle dot or a slash.</span>
        </div>
        {dateErr && <p role="alert" className="text-xs text-destructive mt-1" data-testid="rn-date-error">{dateErr}</p>}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        Plain letters cover 1 to 3,999. Larger numbers, up to 3,999,999, put a bar over the thousands (V with a bar is 5,000). Dates use years 1 to 3,999.
      </p>
      <Suspense fallback={null}>
        <RomanExtras n={numberForSteps} gate={gate} />
      </Suspense>
      <RomanGuide />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
